import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import breakpoints from 'config/breakpoints.json';

// Components
import SelectField from 'components/SelectField';
import Button from './Button';

import styles from './styles.styl';

const filterTypes = {
  button: 'button',
  select: 'select',
};

/** Filters.
 * Компонент работает в двух состояниях - контролируемый и не контролируемый.
 * Если компоненту передать value и onChange, он будет контролируемым.
 * initialValue задается для внутреннего состояния(innerValue)
 */
const Filter = ({
  className,
  selectClassName,
  theme,
  options,
  initialValue,
  value,
  onChange,
  multiRow,
}) => {
  const filterRef = useRef(null);
  const [innerValue, setInnerValue] = useState(initialValue);
  const isControledComponent = useMemo(() => value !== undefined && onChange !== undefined, [
    onChange,
    value,
  ]);
  const componentValue = useMemo(() => (isControledComponent ? value : innerValue), [
    innerValue,
    isControledComponent,
    value,
  ]);

  const handleChange = useCallback(
    newValue => {
      onChange(newValue);

      if (!isControledComponent) {
        setInnerValue(newValue);
      }
    },
    [isControledComponent, onChange],
  );

  const [filterType, setFilterType] = useState(filterTypes.button);
  const innerWidth = useSelector(state => state.responsive.innerWidth);

  useEffect(() => {
    if (innerWidth > breakpoints.verticalSmallTablet) {
      setFilterType(filterTypes.button);
    } else {
      setFilterType(filterTypes.select);
    }
  }, [innerWidth]);

  return (
    <div
      ref={filterRef}
      className={cn(styles.Filter, className, {
        [styles[`Filter_${theme}`]]: theme,
        [styles.Filter_loaded]: true,
        [styles.Filter_multiRow]: multiRow,
      })}
    >
      {filterType === 'button' ? (
        options.map(option => (
          <Button
            key={option.value}
            value={option.value}
            className={styles.Filter__button}
            activeClass={styles.Filter__button_selected}
            selected={option.value === componentValue}
            onClick={handleChange}
          >
            {option.label}{' '}
            {option.count && <span className={styles.Filter__total}>{option.count}</span>}
          </Button>
        ))
      ) : (
        <SelectField
          className={cn(styles.Filter__select, className, selectClassName)}
          name="filter_type"
          value={componentValue}
          onChange={handleChange}
          options={options}
          theme="withBorder"
        />
      )}
    </div>
  );
};

Filter.defaultProps = {
  value: undefined,
  onChange: undefined,
  initialValue: null,
  className: '',
};

Filter.propTypes = {
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  theme: PropTypes.oneOf(['search']),
  initialValue: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  multiRow: PropTypes.bool,
};

export default Filter;
